import {
    closestCenter,
    DndContext,
    type DragEndEvent,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core'
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { type CSSProperties, useEffect, useState } from 'react'

import { t } from '../../../../../../common/i18n.js'
import type { Language } from '../../../../../../common/types/i18n.js'
import {
    LEAVE_BADGE_TYPES,
    LEAVE_CATEGORIES,
    type LeaveCategory,
} from '../../../../../../common/types/portal-leave-types.js'
import { hexBadgeStyle, isHexColor } from '../../../../../../common/utils/badge-color.js'
import { Button } from '../../../../../components/button/button.js'
import { Select } from '../../../../../components/forms/select/select.js'
import { TextField } from '../../../../../components/forms/text-field/text-field.js'
import { DragVerticalIcon } from '../../../../../components/icon/icon.js'
import { Plus, X } from '../../../../../components/icon/solar-icons.js'
import { PortalAbsenceBadge } from '../../../../../components/portal-absence-badge/portal-absence-badge.js'
import {
    createLeavePolicy,
    createSettingsLeaveType,
    deleteSettingsLeaveType,
    fetchLeavePolicies,
    fetchPolicyEmployees,
    fetchSettingsLeaveTypes,
    reorderSettingsLeaveTypes,
    savePolicyEmployees,
    type SettingsLeaveType,
    type SettingsPolicy,
    updateLeavePolicy,
    updateSettingsLeaveType,
} from '../../../utils/portal-api.js'
import { PickerEmployee, PolicyEmployeePicker } from './policy-employee-picker.js'

export interface LeaveTypesSectionProps {
    lang: Language
    showToast: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void
}

// The type's identity plus its single rule set (portal_leave_policies row). The
// two used to be edited in separate tabs; they are one thing to the user, so the
// editor drives both at once (TASK-307 review: everything type-related lives
// under "Eemaloleku tüübid").
interface TypeForm {
    // Type identity (portal_leave_types)
    name: string
    slug: string
    badge_type: string
    is_paid: boolean
    accrues_leave: boolean
    // Work activity vs real leave — BalanceTime, payroll and Forecast key off it.
    category: LeaveCategory
    // Rule set (portal_leave_policies) — accrual_method 'none' means "no limit,
    // no balance tracking"; 'upfront'/'monthly' are the two limited-allocation
    // usage modes.
    accrual_method: string
    annual_limit: number
    carryover_expiry_months: number | null
    allowance_rounding: string
    min_notice_days: number
    min_duration_days: number
    max_duration_days: number | null
    must_take_full_week: boolean
    requires_approval: boolean
    applies_to: string
}

const emptyForm: TypeForm = {
    name: '',
    slug: '',
    badge_type: 'vacation',
    is_paid: true,
    accrues_leave: true,
    category: 'time_off',
    accrual_method: 'none',
    annual_limit: 28,
    carryover_expiry_months: null,
    allowance_rounding: 'half',
    min_notice_days: 0,
    min_duration_days: 1,
    max_duration_days: null,
    must_take_full_week: false,
    requires_approval: true,
    applies_to: 'all',
}

// A carry-over months value that isn't one of the fixed options, so the select
// stays on "custom" while the admin types a number.
const CARRYOVER_CUSTOM_DEFAULT = 6

// The DB driver can hand back decimals as strings — normalise to number|null.
const numOrNull = (v: number | null | undefined): number | null =>
    v === null || v === undefined ? null : Number(v)

const carryoverOption = (months: number | null): string => {
    if (months === null) return 'none'
    if (months === 12) return '12'
    if (months === 18) return '18'
    return 'custom'
}

interface SortableTypeRowProps {
    lt: SettingsLeaveType
    lang: Language
    summary: string
    onEdit: () => void
    onDelete: () => void
}

// One draggable list row; the handle is the only drag activator so the Edit and
// Delete buttons keep working as plain clicks.
const SortableTypeRow = ({ lt, lang, summary, onEdit, onDelete }: SortableTypeRowProps) => {
    const s = t.portal.settings.leaveTypes
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: lt.id,
    })
    const style: CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    }
    return (
        <div ref={setNodeRef} style={style} className="portal-settings__data-row">
            <button
                type="button"
                className="portal-settings__drag-handle"
                aria-label={s.dragToReorder(lang)}
                {...attributes}
                {...listeners}
            >
                <DragVerticalIcon size={16} />
            </button>
            <div className="portal-settings__data-info">
                <div className="portal-settings__data-name">
                    <span style={{ marginRight: 8 }}>
                        <PortalAbsenceBadge
                            badgeType={lt.badge_type}
                            label={s.badgeTypes[lt.badge_type]?.(lang) ?? lt.badge_type}
                        />
                    </span>
                    {lt.name}
                </div>
                <div className="portal-settings__data-meta">
                    {lt.slug} · {s.isPaid(lang)}: {lt.is_paid ? s.yes(lang) : s.no(lang)} ·{' '}
                    {s.accruesLeave(lang)}: {lt.accrues_leave ? s.yes(lang) : s.no(lang)} ·{' '}
                    {lt.category === 'working' && <>{s.categoryWorking(lang)} · </>}
                    {summary}
                </div>
            </div>
            <div className="portal-settings__data-actions">
                <button type="button" onClick={onEdit}>
                    {s.edit(lang)}
                </button>
                <button type="button" className="is-danger" onClick={onDelete}>
                    {s.delete(lang)}
                </button>
            </div>
        </div>
    )
}

const slugify = (s: string): string =>
    s
        .toLowerCase()
        .replace(/[äöüõ]/g, (c) => ({ ä: 'a', ö: 'o', ü: 'u', õ: 'o' })[c] ?? c)
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')

export const LeaveTypesSection = ({ lang, showToast }: LeaveTypesSectionProps) => {
    const s = t.portal.settings.leaveTypes
    const p = t.portal.settings.policies
    const [types, setTypes] = useState<SettingsLeaveType[]>([])
    const [policies, setPolicies] = useState<SettingsPolicy[]>([])
    const [slideOpen, setSlideOpen] = useState(false)
    const [editing, setEditing] = useState<SettingsLeaveType | null>(null)
    const [form, setForm] = useState<TypeForm>(emptyForm)
    const [saving, setSaving] = useState(false)
    const [policyEmployeeIds, setPolicyEmployeeIds] = useState<number[]>([])
    const [policySelectedEmployees, setPolicySelectedEmployees] = useState<PickerEmployee[]>([])
    // The distance constraint keeps plain clicks on the row's buttons working.
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }))

    // The one rule set for a type. The canonical model is 1 policy per type; if
    // several exist the lowest id wins (fetchLeavePolicies is ordered by id).
    const policyForType = (typeId: number): SettingsPolicy | undefined =>
        policies.find((pol) => pol.leave_type_id === typeId)

    const reload = async () => {
        const [typeRes, policyRes] = await Promise.all([
            fetchSettingsLeaveTypes(),
            fetchLeavePolicies(),
        ])
        if (typeRes.ok) setTypes(typeRes.data)
        if (policyRes.ok) setPolicies(policyRes.data)
    }

    useEffect(() => {
        void reload()
    }, [])

    const openCreate = () => {
        setEditing(null)
        setForm(emptyForm)
        setPolicyEmployeeIds([])
        setPolicySelectedEmployees([])
        setSlideOpen(true)
    }

    const openEdit = (lt: SettingsLeaveType) => {
        setEditing(lt)
        const policy = policyForType(lt.id)
        setForm({
            name: lt.name,
            slug: lt.slug,
            badge_type: lt.badge_type,
            is_paid: lt.is_paid,
            accrues_leave: lt.accrues_leave,
            category: lt.category,
            accrual_method: policy?.accrual_method ?? 'none',
            annual_limit: policy ? Number(policy.annual_limit) : 28,
            carryover_expiry_months: numOrNull(policy?.carryover_expiry_months),
            allowance_rounding: policy?.allowance_rounding ?? 'half',
            min_notice_days: policy ? Number(policy.min_notice_days) : 0,
            min_duration_days: policy ? Number(policy.min_duration_days) : 1,
            max_duration_days: numOrNull(policy?.max_duration_days),
            must_take_full_week: policy?.must_take_full_week ?? false,
            requires_approval: policy?.requires_approval ?? true,
            applies_to: policy?.applies_to ?? 'all',
        })
        setPolicyEmployeeIds([])
        setPolicySelectedEmployees([])
        if (policy?.applies_to === 'custom') {
            void (async () => {
                const r = await fetchPolicyEmployees(policy.id)
                if (r.ok) {
                    setPolicyEmployeeIds(r.data.map((e) => e.id))
                    setPolicySelectedEmployees(r.data)
                }
            })()
        }
        setSlideOpen(true)
    }

    const close = () => {
        setSlideOpen(false)
        setEditing(null)
    }

    // Rule-set fields sent to the policy endpoints. A no-limit type carries no
    // annual allowance or carry-over. The name rides along so renaming the type
    // renames its rule set too — displays that read the policy row (TASK-400).
    const policyPayload = () => ({
        name: form.name,
        accrual_method: form.accrual_method,
        annual_limit: form.accrual_method === 'none' ? 0 : form.annual_limit,
        carryover_expiry_months:
            form.accrual_method === 'none' ? null : form.carryover_expiry_months,
        allowance_rounding: form.allowance_rounding,
        min_notice_days: form.min_notice_days,
        min_duration_days: form.min_duration_days,
        max_duration_days: form.max_duration_days,
        must_take_full_week: form.must_take_full_week,
        requires_approval: form.requires_approval,
        applies_to: form.applies_to,
    })

    const save = async () => {
        if (!form.name || !form.badge_type) return
        setSaving(true)
        try {
            // Which type + policy we end up writing the rule set to.
            let typeId: number
            if (editing) {
                const r = await updateSettingsLeaveType(editing.id, {
                    name: form.name,
                    badge_type: form.badge_type,
                    is_paid: form.is_paid,
                    accrues_leave: form.accrues_leave,
                    category: form.category,
                })
                if (!r.ok) {
                    showToast(r.error, 'error')
                    return
                }
                typeId = editing.id
            } else {
                const r = await createSettingsLeaveType({
                    name: form.name,
                    slug: form.slug || undefined,
                    badge_type: form.badge_type,
                    is_paid: form.is_paid,
                    accrues_leave: form.accrues_leave,
                    category: form.category,
                })
                if (!r.ok) {
                    showToast(r.error, 'error')
                    return
                }
                typeId = r.data.id
            }

            // One rule set per type: update it if it exists, otherwise create it
            // (this normalises the legacy types that have no policy row yet).
            const existingPolicy = editing ? policyForType(editing.id) : undefined
            let policyId: number | undefined = existingPolicy?.id
            if (existingPolicy) {
                const r = await updateLeavePolicy(existingPolicy.id, policyPayload())
                if (!r.ok) {
                    showToast(r.error, 'error')
                    return
                }
            } else {
                const r = await createLeavePolicy({
                    leave_type_id: typeId,
                    ...policyPayload(),
                })
                if (!r.ok) {
                    showToast(r.error, 'error')
                    return
                }
                policyId = r.data.id
            }

            if (form.applies_to === 'custom' && policyId !== undefined) {
                await savePolicyEmployees(policyId, policyEmployeeIds)
            }

            showToast(editing ? s.updated(lang) : s.created(lang), 'success')
            close()
            await reload()
        } finally {
            setSaving(false)
        }
    }

    const handleDelete = async (lt: SettingsLeaveType) => {
        const r = await deleteSettingsLeaveType(lt.id)
        if (!r.ok) {
            showToast(r.error, 'error')
            return
        }
        showToast(s.deleted(lang), 'success')
        await reload()
    }

    const allocationSummary = (lt: SettingsLeaveType): string => {
        const policy = policyForType(lt.id)
        if (!policy || policy.accrual_method === 'none') return p.allocationNoLimit(lang)
        return `${policy.annual_limit} ${s.daysShort(lang)}`
    }

    // Optimistic reorder: the list moves immediately, the server write follows;
    // on failure the previous order is restored. TASK-342 — this order drives
    // the booking dropdown and every other type listing.
    const handleDragEnd = (e: DragEndEvent) => {
        const over = e.over
        if (!over || e.active.id === over.id) return
        const oldIndex = types.findIndex((lt) => lt.id === e.active.id)
        const newIndex = types.findIndex((lt) => lt.id === over.id)
        if (oldIndex < 0 || newIndex < 0) return
        const previous = types
        const next = arrayMove(types, oldIndex, newIndex)
        setTypes(next)
        void (async () => {
            const r = await reorderSettingsLeaveTypes(next.map((lt) => lt.id))
            if (!r.ok) {
                showToast(r.error, 'error')
                setTypes(previous)
                return
            }
            showToast(s.orderUpdated(lang), 'success')
        })()
    }

    return (
        <div>
            <div className="portal-settings__section-header">
                <h3 className="portal-settings__section-title">{s.title(lang)}</h3>
                <Button
                    variant="primary"
                    size="small"
                    iconLeft={Plus}
                    text={s.createType(lang)}
                    onClick={openCreate}
                />
            </div>

            {types.length === 0 ? (
                <div className="portal-settings__section-empty">{s.noTypes(lang)}</div>
            ) : (
                <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <SortableContext
                        items={types.map((lt) => lt.id)}
                        strategy={verticalListSortingStrategy}
                    >
                        <div className="portal-settings__data-list">
                            {types.map((lt) => (
                                <SortableTypeRow
                                    key={lt.id}
                                    lt={lt}
                                    lang={lang}
                                    summary={allocationSummary(lt)}
                                    onEdit={() => openEdit(lt)}
                                    onDelete={() => void handleDelete(lt)}
                                />
                            ))}
                        </div>
                    </SortableContext>
                </DndContext>
            )}

            {slideOpen ? (
                <>
                    <div className="portal-slide-overlay" onClick={close} />
                    <div className="portal-slide-over">
                        <div className="portal-slide-over__header">
                            <h3 className="portal-slide-over__title">
                                {editing ? s.editType(lang) : s.createType(lang)}
                            </h3>
                            <Button
                                variant="tertiary"
                                size="small"
                                iconOnly
                                iconLeft={X}
                                text={t.portal.settings.close(lang)}
                                onClick={close}
                            />
                        </div>
                        <div className="portal-slide-over__body">
                            <div className="portal-slide-over__field">
                                <TextField
                                    label={s.name(lang)}
                                    value={form.name}
                                    onChange={(value) =>
                                        setForm((f) => ({
                                            ...f,
                                            name: value,
                                            slug: editing ? f.slug : slugify(value),
                                        }))
                                    }
                                />
                            </div>

                            {!editing ? (
                                <div className="portal-slide-over__field">
                                    <TextField
                                        label={s.slug(lang)}
                                        value={form.slug}
                                        onChange={(value) =>
                                            setForm((f) => ({ ...f, slug: value }))
                                        }
                                        note={{ text: s.slugHint(lang) }}
                                    />
                                </div>
                            ) : null}

                            <div className="portal-slide-over__field">
                                <label>{s.badgeType(lang)}</label>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexWrap: 'wrap',
                                        gap: 6,
                                        marginBottom: 8,
                                    }}
                                >
                                    {LEAVE_BADGE_TYPES.map((bt) => (
                                        <button
                                            key={bt}
                                            type="button"
                                            className={`portal-absence-badge portal-absence-badge--${bt}`}
                                            onClick={() =>
                                                setForm((f) => ({ ...f, badge_type: bt }))
                                            }
                                            style={{
                                                cursor: 'pointer',
                                                border:
                                                    form.badge_type === bt
                                                        ? '2px solid var(--portal-accent)'
                                                        : '2px solid transparent',
                                            }}
                                        >
                                            {s.badgeTypes[bt]?.(lang) ?? bt}
                                        </button>
                                    ))}
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                    }}
                                >
                                    <label
                                        style={{
                                            fontSize: '0.875rem',
                                            color: 'var(--portal-text-secondary)',
                                        }}
                                    >
                                        {s.customColor(lang)}:
                                    </label>
                                    <input
                                        type="color"
                                        value={
                                            isHexColor(form.badge_type)
                                                ? form.badge_type
                                                : '#6366f1'
                                        }
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                badge_type: e.target.value.toLowerCase(),
                                            }))
                                        }
                                        style={{
                                            width: 40,
                                            height: 28,
                                            padding: 0,
                                            border: '1px solid var(--portal-border)',
                                            borderRadius: 4,
                                            cursor: 'pointer',
                                        }}
                                    />
                                    <input
                                        className="portal-settings__input"
                                        type="text"
                                        value={form.badge_type}
                                        maxLength={32}
                                        onChange={(e) =>
                                            setForm((f) => ({
                                                ...f,
                                                badge_type: e.target.value,
                                            }))
                                        }
                                        style={{ width: 140 }}
                                    />
                                    {isHexColor(form.badge_type) ? (
                                        <span
                                            className="portal-absence-badge"
                                            style={hexBadgeStyle(form.badge_type) ?? undefined}
                                        >
                                            {s.preview(lang)}
                                        </span>
                                    ) : null}
                                </div>
                            </div>

                            <div className="portal-slide-over__field">
                                <Select
                                    label={s.isPaid(lang)}
                                    value={form.is_paid ? 'yes' : 'no'}
                                    onChange={(value) =>
                                        setForm((f) => ({ ...f, is_paid: value === 'yes' }))
                                    }
                                    options={[
                                        { value: 'yes', label: s.yes(lang) },
                                        { value: 'no', label: s.no(lang) },
                                    ]}
                                />
                            </div>

                            <div className="portal-slide-over__field">
                                <Select
                                    label={s.accruesLeave(lang)}
                                    value={form.accrues_leave ? 'yes' : 'no'}
                                    onChange={(value) =>
                                        setForm((f) => ({ ...f, accrues_leave: value === 'yes' }))
                                    }
                                    options={[
                                        { value: 'yes', label: s.yes(lang) },
                                        { value: 'no', label: s.no(lang) },
                                    ]}
                                    note={{ text: s.accruesLeaveHint(lang) }}
                                />
                            </div>

                            <div className="portal-slide-over__field">
                                <Select<LeaveCategory>
                                    label={s.category(lang)}
                                    value={form.category}
                                    onChange={(value) =>
                                        setForm((f) => ({ ...f, category: value }))
                                    }
                                    options={LEAVE_CATEGORIES.map((c) => ({
                                        value: c,
                                        label:
                                            c === 'working'
                                                ? s.categoryWorking(lang)
                                                : s.categoryTimeOff(lang),
                                    }))}
                                    note={{ text: s.categoryHint(lang) }}
                                />
                            </div>

                            {/* Rule set (was the separate "Tüüpide seaded" editor). */}
                            <div className="portal-slide-over__field">
                                <Select
                                    label={p.allocationRule(lang)}
                                    value={form.accrual_method === 'none' ? 'no_limit' : 'limited'}
                                    onChange={(value) =>
                                        setForm((f) => ({
                                            ...f,
                                            accrual_method:
                                                value === 'no_limit' ? 'none' : 'upfront',
                                        }))
                                    }
                                    options={[
                                        { value: 'no_limit', label: p.allocationNoLimit(lang) },
                                        { value: 'limited', label: p.allocationLimited(lang) },
                                    ]}
                                />
                            </div>

                            {form.accrual_method !== 'none' ? (
                                <>
                                    <div className="portal-slide-over__field">
                                        <TextField
                                            label={p.annualLimit(lang)}
                                            inputProps={{ type: 'number' }}
                                            value={String(form.annual_limit)}
                                            onChange={(value) =>
                                                setForm((f) => ({
                                                    ...f,
                                                    annual_limit: Number(value),
                                                }))
                                            }
                                        />
                                    </div>
                                    <div className="portal-slide-over__field">
                                        <Select
                                            label={p.usage(lang)}
                                            value={form.accrual_method}
                                            onChange={(value) =>
                                                setForm((f) => ({ ...f, accrual_method: value }))
                                            }
                                            options={[
                                                {
                                                    value: 'upfront',
                                                    label: p.usageFullAllocation(lang),
                                                },
                                                {
                                                    value: 'monthly',
                                                    label: p.usageAccruedOnly(lang),
                                                },
                                            ]}
                                        />
                                    </div>
                                    <div className="portal-slide-over__field">
                                        <Select
                                            label={p.carryover(lang)}
                                            value={carryoverOption(form.carryover_expiry_months)}
                                            onChange={(value) =>
                                                setForm((f) => ({
                                                    ...f,
                                                    carryover_expiry_months:
                                                        value === 'none'
                                                            ? null
                                                            : value === 'custom'
                                                              ? CARRYOVER_CUSTOM_DEFAULT
                                                              : Number(value),
                                                }))
                                            }
                                            options={[
                                                { value: 'none', label: p.carryoverNone(lang) },
                                                { value: '12', label: p.carryover12(lang) },
                                                { value: '18', label: p.carryover18(lang) },
                                                { value: 'custom', label: p.carryoverCustom(lang) },
                                            ]}
                                        />
                                    </div>
                                    {carryoverOption(form.carryover_expiry_months) === 'custom' ? (
                                        <div className="portal-slide-over__field">
                                            <TextField
                                                label={p.carryoverCustomMonths(lang)}
                                                inputProps={{ type: 'number' }}
                                                value={String(
                                                    form.carryover_expiry_months ??
                                                        CARRYOVER_CUSTOM_DEFAULT,
                                                )}
                                                onChange={(value) =>
                                                    setForm((f) => ({
                                                        ...f,
                                                        carryover_expiry_months: Math.max(
                                                            1,
                                                            Number(value) || 1,
                                                        ),
                                                    }))
                                                }
                                            />
                                        </div>
                                    ) : null}
                                    <div className="portal-slide-over__field">
                                        <Select
                                            label={p.allowanceRounding(lang)}
                                            value={form.allowance_rounding}
                                            onChange={(value) =>
                                                setForm((f) => ({
                                                    ...f,
                                                    allowance_rounding: value,
                                                }))
                                            }
                                            options={[
                                                { value: 'half', label: p.roundingHalf(lang) },
                                                { value: 'full', label: p.roundingFull(lang) },
                                                { value: 'none', label: p.roundingNone(lang) },
                                            ]}
                                        />
                                    </div>
                                </>
                            ) : null}

                            <div className="portal-slide-over__field">
                                <TextField
                                    label={p.minNoticeDays(lang)}
                                    inputProps={{ type: 'number' }}
                                    value={String(form.min_notice_days)}
                                    onChange={(value) =>
                                        setForm((f) => ({
                                            ...f,
                                            min_notice_days: Number(value),
                                        }))
                                    }
                                />
                            </div>
                            <div className="portal-slide-over__field">
                                <TextField
                                    label={p.minDurationDays(lang)}
                                    inputProps={{ type: 'number' }}
                                    value={String(form.min_duration_days)}
                                    onChange={(value) =>
                                        setForm((f) => ({
                                            ...f,
                                            min_duration_days: Number(value),
                                        }))
                                    }
                                />
                            </div>
                            <div className="portal-slide-over__field">
                                <TextField
                                    label={p.maxDurationDays(lang)}
                                    inputProps={{ type: 'number' }}
                                    value={
                                        form.max_duration_days === null
                                            ? ''
                                            : String(form.max_duration_days)
                                    }
                                    onChange={(value) =>
                                        setForm((f) => ({
                                            ...f,
                                            max_duration_days:
                                                value === '' || Number(value) <= 0
                                                    ? null
                                                    : Number(value),
                                        }))
                                    }
                                />
                            </div>
                            <div className="portal-slide-over__field">
                                <Select
                                    label={p.mustTakeFullWeek(lang)}
                                    value={form.must_take_full_week ? 'yes' : 'no'}
                                    onChange={(value) =>
                                        setForm((f) => ({
                                            ...f,
                                            must_take_full_week: value === 'yes',
                                        }))
                                    }
                                    options={[
                                        { value: 'no', label: s.no(lang) },
                                        { value: 'yes', label: s.yes(lang) },
                                    ]}
                                />
                            </div>
                            <div className="portal-slide-over__field">
                                <Select
                                    label={p.requiresApproval(lang)}
                                    value={form.requires_approval ? 'yes' : 'no'}
                                    onChange={(value) =>
                                        setForm((f) => ({
                                            ...f,
                                            requires_approval: value === 'yes',
                                        }))
                                    }
                                    options={[
                                        { value: 'yes', label: s.yes(lang) },
                                        { value: 'no', label: s.no(lang) },
                                    ]}
                                />
                            </div>
                            <div className="portal-slide-over__field">
                                <Select
                                    label={p.appliesTo(lang)}
                                    value={form.applies_to}
                                    onChange={(value) =>
                                        setForm((f) => ({ ...f, applies_to: value }))
                                    }
                                    options={[
                                        { value: 'all', label: p.appliesToAll(lang) },
                                        { value: 'intern', label: p.appliesToIntern(lang) },
                                        { value: 'custom', label: p.appliesToCustom(lang) },
                                    ]}
                                />
                            </div>
                            {form.applies_to === 'custom' ? (
                                <div className="portal-slide-over__field">
                                    <label>{p.assignedEmployees(lang)}</label>
                                    <PolicyEmployeePicker
                                        lang={lang}
                                        selectedIds={policyEmployeeIds}
                                        selectedEmployees={policySelectedEmployees}
                                        onAdd={(emp) => {
                                            setPolicyEmployeeIds((prev) =>
                                                prev.includes(emp.id) ? prev : [...prev, emp.id],
                                            )
                                            setPolicySelectedEmployees((prev) =>
                                                prev.some((e) => e.id === emp.id)
                                                    ? prev
                                                    : [...prev, emp],
                                            )
                                        }}
                                        onRemove={(id) => {
                                            setPolicyEmployeeIds((prev) =>
                                                prev.filter((eid) => eid !== id),
                                            )
                                            setPolicySelectedEmployees((prev) =>
                                                prev.filter((e) => e.id !== id),
                                            )
                                        }}
                                        onSelectAll={(employees) => {
                                            setPolicyEmployeeIds((prev) => {
                                                const set = new Set(prev)
                                                for (const e of employees) set.add(e.id)
                                                return [...set]
                                            })
                                            setPolicySelectedEmployees((prev) => {
                                                const existing = new Set(prev.map((e) => e.id))
                                                return [
                                                    ...prev,
                                                    ...employees.filter((e) => !existing.has(e.id)),
                                                ]
                                            })
                                        }}
                                        onClearAll={() => {
                                            setPolicyEmployeeIds([])
                                            setPolicySelectedEmployees([])
                                        }}
                                    />
                                </div>
                            ) : null}
                        </div>
                        <div className="portal-slide-over__footer">
                            <Button
                                variant="tertiary"
                                size="small"
                                text={t.portal.settings.close(lang)}
                                onClick={close}
                            />
                            <Button
                                variant="primary"
                                size="small"
                                text={saving ? s.saving(lang) : s.save(lang)}
                                isDisabled={saving || !form.name}
                                onClick={() => void save()}
                            />
                        </div>
                    </div>
                </>
            ) : null}
        </div>
    )
}
